// @flow

import * as React from 'react';
import keycode from 'keycode';

import Tab from '@zapier/common-components/src/components/Tabs/Tab';
import TabList from '@zapier/common-components/src/components/Tabs/TabList';
import TabTitle from '@zapier/common-components/src/components/Tabs/TabTitle';
import { ensureArray } from '@zapier/common-utils';

type TabsPropTypes = {
  /** Array of Tab components */
  children?: Array<React.Element<typeof Tab>> | React.Element<typeof Tab>,
};

type TabsStateType = {
  selectedIndex: number,
};

let currentId = 0;
const generateUniqueId = () => `tabs-${++currentId}`;

/**
 * `Tabs` controls its own active tab state and will manage
 * hidden / shown `Tab`s along with their associated `TabList`
 */
class Tabs extends React.Component<TabsPropTypes, TabsStateType> {
  static Tab = Tab;

  state = {
    selectedIndex: 0,
  };

  // We need a unique id in case there are multiple instances on the page
  id = generateUniqueId();

  getChildren = (): Array<React.Element<typeof Tab>> => {
    const { children } = this.props;
    if (!children) return [];
    return ensureArray(children);
  };

  getId = (index: number) => `${this.id}-${index}`;

  getNumberOfTabs = () => this.getChildren().length;

  /**
   * For ARIA support
   * see https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-1/tabs.html
   */
  handleKeyDown = (index: number) => (e: SyntheticKeyboardEvent<>) => {
    let nextIndex = this.state.selectedIndex;
    let preventDefault = true;
    const tabCount = this.getNumberOfTabs();

    switch (e.keyCode) {
      case keycode('enter'):
      case keycode('space'):
        // Switch to focused tab
        nextIndex = index;
        break;
      case keycode('left'):
      case keycode('up'):
        // Previous tab (looping around if required)
        nextIndex = nextIndex === 0 ? tabCount - 1 : nextIndex - 1;
        break;
      case keycode('right'):
      case keycode('down'):
        // Next tab (looping around if required)
        nextIndex = nextIndex === tabCount - 1 ? 0 : nextIndex + 1;
        break;
      case keycode('end'):
        // Last tab
        nextIndex = tabCount - 1;
        break;
      case keycode('home'):
        // First tab
        nextIndex = 0;
        break;
      default:
        // Pass through
        preventDefault = false;
        break;
    }

    if (preventDefault) {
      e.preventDefault();
    }

    if (nextIndex !== this.state.selectedIndex) {
      this.selectTab(nextIndex);
    }
  };

  handleTabClick = (selectedIndex: number) => () =>
    this.selectTab(selectedIndex);

  selectTab = (selectedIndex: number) => this.setState({ selectedIndex });

  render() {
    const { selectedIndex } = this.state;
    const children = this.getChildren();

    // We want to prevent rendering an empty div with a border (which just
    // looks like a line to the user)
    if (!children.length) return null;

    const panels = children.map((child: React.Element<typeof Tab>, i: number) =>
      // Add isActive and key props to each Tab
      React.cloneElement(child, {
        id: this.getId(i),
        isActive: i === selectedIndex,
        key: i,
      })
    );

    const tabs = children.map(
      ({ props }: React.Element<typeof Tab>, i: number) => (
        <TabTitle
          children={props.title}
          icon={props.icon}
          id={this.getId(i)}
          isActive={selectedIndex === i}
          key={i}
          onClick={this.handleTabClick(i)}
          onKeyDown={this.handleKeyDown(i)}
        />
      )
    );

    return (
      <div>
        <TabList>{tabs}</TabList>
        {panels}
      </div>
    );
  }
}

export default Tabs;
